import React from "react";
import { Turnstile } from "./shared-ui.jsx";

// BookingCalendar — custom UI + Cal.com v2 via Netlify proxy.
// Endpoints: GET /api/cal-slots?start&end&tz   POST /api/cal-book

const TZ = (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) || "Europe/Warsaw";
const PL_MONTHS = ["Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec","Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"];
const PL_WEEKDAYS = ["P","W","Ś","C","P","S","N"]; // Monday-first

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
const startOfMonth = (y, m) => new Date(y, m, 1);
const endOfMonth = (y, m) => new Date(y, m+1, 0);
const today0 = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };

// Builds a 6×7 grid of Date objects (Monday-first) for given month.
function monthGrid(year, month) {
  const first = startOfMonth(year, month);
  const offset = (first.getDay() + 6) % 7; // Mon=0 .. Sun=6
  const gridStart = new Date(year, month, 1 - offset);
  return Array.from({length: 42}, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
}

function formatTime(iso, tz) {
  try {
    return new Intl.DateTimeFormat("pl-PL", { hour: "2-digit", minute: "2-digit", timeZone: tz }).format(new Date(iso));
  } catch { return new Date(iso).toTimeString().slice(0,5); }
}

function BookingCalendar() {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth());
  const [slotsByDate, setSlotsByDate] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(null); // "YYYY-MM-DD"
  const [selectedSlot, setSelectedSlot] = React.useState(null); // ISO string
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", notes: "", hp: "" });
  const [formErr, setFormErr] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [confirmed, setConfirmed] = React.useState(null); // booking object
  const [turnstileToken, setTurnstileToken] = React.useState(null);

  const monthStart = startOfMonth(year, month);
  const monthEnd = endOfMonth(year, month);
  const canGoPrev = monthStart > startOfMonth(now.getFullYear(), now.getMonth());

  // Fetch slots whenever month changes.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const fromD = monthStart < today0() ? today0() : monthStart;
    const params = new URLSearchParams({
      start: ymd(fromD),
      end: ymd(monthEnd),
      tz: TZ,
    });
    fetch(`/api/cal-slots?${params}`, { cache: "no-store" })
      .then(async (res) => {
        const ct = res.headers.get("content-type") || "";
        const body = ct.includes("application/json") ? await res.json().catch(() => ({})) : { error: await res.text().catch(() => "") };
        if (!res.ok) {
          const msg = body.detail || body.error || `HTTP ${res.status}`;
          throw new Error(msg);
        }
        if (!ct.includes("application/json")) throw new Error("non-json response");
        return body;
      })
      .then((data) => {
        if (cancelled) return;
        const payload = (data && (data.data || data.slots || data)) || {};
        const normalized = {};
        Object.keys(payload).forEach((dateKey) => {
          const arr = payload[dateKey];
          if (Array.isArray(arr)) {
            normalized[dateKey] = arr
              .map((s) => (typeof s === "string" ? s : s.start || s.time))
              .filter(Boolean);
          }
        });
        setSlotsByDate(normalized);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e.message || String(e));
        setSlotsByDate({});
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [year, month]);

  const grid = React.useMemo(() => monthGrid(year, month), [year, month]);
  const todayKey = ymd(today0());

  const changeMonth = (delta) => {
    setSelectedDate(null);
    setSelectedSlot(null);
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const pickDate = (key) => {
    setSelectedDate(key);
    setSelectedSlot(null);
  };

  const pickSlot = (iso) => {
    setSelectedSlot(iso);
    setSubmitError(null);
  };

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Podaj imię.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Podaj poprawny email.";
    if ((form.phone.replace(/\D/g, "") || "").length < 9) err.phone = "Podaj numer telefonu (min. 9 cyfr).";
    if (form.notes.trim().length < 10) err.notes = "Wiadomość musi mieć co najmniej 10 znaków — domena, problem, skala.";
    if (form.notes.trim().length > 4000) err.notes = "Wiadomość przekracza limit 4 000 znaków.";
    setFormErr(err);
    if (Object.keys(err).length) { setSubmitError(null); return; }
    if (!turnstileToken) { setSubmitError("Trwa weryfikacja antybotowa — spróbuj ponownie za chwilę."); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/cal-book", {
        method: "POST",
        cache: "no-store",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          start: selectedSlot,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          notes: form.notes.trim(),
          timeZone: TZ,
          hp: form.hp,
          turnstileToken,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 429) {
          setSubmitError("Zbyt wiele prób w krótkim czasie. Poczekaj minutę i spróbuj ponownie.");
        } else if (res.status === 403 && /antybotow|turnstile/i.test(data.error || "")) {
          setSubmitError("Weryfikacja antybotowa wygasła. Odśwież stronę i wyślij ponownie.");
          setTurnstileToken(null);
          if (window.turnstile?.reset) try { window.turnstile.reset(); } catch {}
        } else if (res.status === 400 && data.error) {
          // Map server validation errors back to per-field errors where possible.
          const map = { name: "name", email: "email", phone: "phone", notes: "notes" };
          const matched = Object.keys(map).find(k => new RegExp(`Invalid ${k}`, "i").test(data.error));
          if (matched) {
            setFormErr({ [matched]: "Pole nie spełnia wymagań — sprawdź zawartość." });
          } else {
            setSubmitError("Niektóre pola są niepoprawne. Sprawdź formularz i spróbuj ponownie.");
          }
        } else {
          setSubmitError("Nie udało się zarezerwować. Spróbuj ponownie lub napisz na hello@letscommerce.pl.");
        }
        return;
      }
      setConfirmed({ when: selectedSlot, email: form.email.trim() });
    } catch (e) {
      setSubmitError("Brak połączenia. Sprawdź internet i spróbuj ponownie.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setConfirmed(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ name: "", email: "", phone: "", notes: "", hp: "" });
    setFormErr({});
    setTurnstileToken(null);
  };

  if (confirmed) {
    return (
      <div className="bk-wrap">
        <div className="bk-head">
          <span className="bk-kicker">Cal.com · Let's commerce</span>
          <span className="bk-meta">30 min · Bezpłatna</span>
        </div>
        <div className="bk-confirm">
          <div className="bk-check">✓</div>
          <h3>Termin zarezerwowany.</h3>
          <p>Wysłaliśmy potwierdzenie na <strong>{confirmed.email}</strong>.</p>
          <p className="bk-confirm-when">
            {new Intl.DateTimeFormat("pl-PL", { dateStyle: "full", timeStyle: "short", timeZone: TZ }).format(new Date(confirmed.when))}
          </p>
          <button type="button" className="lc-btn lc-btn-ghost" onClick={reset}>Zarezerwuj kolejny</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bk-wrap">
      <div className="bk-head">
        <span className="bk-kicker">Cal.com · Let's commerce</span>
        <span className="bk-meta">30 min · Bezpłatna</span>
      </div>

      <div className="bk-body">
        <div className="bk-cal">
          <div className="bk-monthnav">
            <button type="button" className="bk-navbtn" onClick={() => changeMonth(-1)} disabled={!canGoPrev} aria-label="Poprzedni miesiąc">‹</button>
            <span className="bk-monthlabel">{PL_MONTHS[month]} {year}</span>
            <button type="button" className="bk-navbtn" onClick={() => changeMonth(1)} aria-label="Następny miesiąc">›</button>
          </div>

          <div className="bk-weekdays">
            {PL_WEEKDAYS.map((d, i) => <div key={i} className="bk-wd">{d}</div>)}
          </div>

          <div className="bk-grid">
            {grid.map((d) => {
              const key = ymd(d);
              const inMonth = sameMonth(d, monthStart);
              const isPast = d < today0();
              const slots = slotsByDate[key] || [];
              const hasSlots = slots.length > 0;
              const avail = inMonth && !isPast && hasSlots;
              const isSel = selectedDate === key;
              const isToday = key === todayKey;
              const cls = [
                "bk-day",
                !inMonth && "bk-day-muted",
                avail && "bk-day-avail",
                isSel && "bk-day-sel",
                isToday && "bk-day-today",
                (!avail && inMonth) && "bk-day-off",
              ].filter(Boolean).join(" ");
              return (
                <button
                  key={key}
                  type="button"
                  className={cls}
                  disabled={!avail}
                  onClick={() => avail && pickDate(key)}
                  aria-label={`${d.getDate()} ${PL_MONTHS[d.getMonth()]}${hasSlots ? ", " + slots.length + " wolnych terminów" : ", brak terminów"}`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {loading && <div className="bk-note">Ładowanie terminów…</div>}
          {loadError && !loading && (
            <div className="bk-note bk-note-err">
              Nie udało się pobrać terminów ({loadError}). Kalendarz aktywuje się po wdrożeniu na Netlify z ustawionymi zmiennymi środowiskowymi.
            </div>
          )}
        </div>

        <div className="bk-side">
          {!selectedDate && (
            <div className="bk-side-empty">
              <span className="eyebrow">Wybierz dzień</span>
              <p>Kliknij podświetloną datę w kalendarzu, żeby zobaczyć wolne godziny.</p>
            </div>
          )}

          {selectedDate && !selectedSlot && (
            <>
              <span className="eyebrow">Wolne godziny</span>
              <h4 className="bk-side-head">{new Intl.DateTimeFormat("pl-PL", { weekday:"long", day:"numeric", month:"long", timeZone: TZ }).format(new Date(selectedDate + "T12:00:00"))}</h4>
              <div className="bk-slots">
                {(slotsByDate[selectedDate] || []).map((iso) => (
                  <button type="button" key={iso} className="bk-slot" onClick={() => pickSlot(iso)}>
                    {formatTime(iso, TZ)}
                  </button>
                ))}
              </div>
            </>
          )}

          {selectedSlot && (
            <form className="bk-form" onSubmit={submit} noValidate>
              <div className="bk-selected">
                <span className="eyebrow">Termin</span>
                <div className="bk-selected-when">
                  {new Intl.DateTimeFormat("pl-PL", { dateStyle:"full", timeStyle:"short", timeZone: TZ }).format(new Date(selectedSlot))}
                </div>
                <button type="button" className="bk-change" onClick={() => { setSelectedSlot(null); }}>Zmień</button>
              </div>
              <div className={"field" + (formErr.name ? " has-err" : "")}>
                <label>Imię</label>
                <input value={form.name} onChange={onField("name")} placeholder="Jan"/>
                <span className="err">{formErr.name}</span>
              </div>
              <div className={"field" + (formErr.email ? " has-err" : "")}>
                <label>Email</label>
                <input type="email" value={form.email} onChange={onField("email")} placeholder="jan@firma.pl"/>
                <span className="err">{formErr.email}</span>
              </div>
              <div className={"field" + (formErr.phone ? " has-err" : "")}>
                <label>Telefon</label>
                <input type="tel" value={form.phone} onChange={onField("phone")} placeholder="+48 600 000 000" autoComplete="tel"/>
                <span className="err">{formErr.phone}</span>
              </div>
              <div className={"field" + (formErr.notes ? " has-err" : "")}>
                <label>O firmie</label>
                <textarea value={form.notes} onChange={onField("notes")} placeholder="Domena, problem, skala — krótko, żeby rozmowa miała sens."/>
                <span className="err">{formErr.notes}</span>
              </div>
              {/* Honeypot — zostawiamy puste, boty wypełnią. */}
              <div className="hp-field" aria-hidden="true">
                <label htmlFor="bk-hp">Strona WWW (zostaw puste)</label>
                <input id="bk-hp" type="text" tabIndex="-1" autoComplete="off" value={form.hp} onChange={onField("hp")} />
              </div>
              <Turnstile onToken={setTurnstileToken} />
              {submitError && <div className="bk-note bk-note-err">{submitError}</div>}
              <button type="submit" className="lc-btn lc-btn-primary lc-btn-lg" disabled={submitting}>
                {submitting && <span className="lc-spin" aria-hidden="true"/>}
                {submitting ? "Rezerwuję…" : "Zarezerwuj"} <span className="lc-btn-arr">↗</span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export { BookingCalendar };
